import { CascadiaColor, CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React from "react";

import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";

export const SectionMessageTypes = [
  "default",
  "success",
  "info",
  "warning",
  "danger",
] as const;

export type SectionMessageType = (typeof SectionMessageTypes)[number];

export interface SectionMessageProps {
  sectionTitle?: string;
  children?: React.ReactNode;
  icon?: CascadiaStandardIcon;
  type?: SectionMessageType;
  fontSize?: "sm" | "md";
  testID?: string;
  noBorderRadius?: boolean;
}

export function SectionMessage(props: SectionMessageProps) {
  const {
    sectionTitle,
    children,
    icon,
    type = "default",
    testID = "section-message",
    fontSize = "sm",
    noBorderRadius,
  } = props;
  const iconColorsByType = {
    default: "icon-base",
    success: "icon-success",
    info: "icon-info",
    danger: "icon-danger",
    warning: "icon-warning",
  };
  const colorsByType = {
    default: "var(--background-accent-neutral-weak)",
    success: "var(--background-success-weak)",
    info: "var(--background-info-weak)",
    danger: "var(--background-danger-weak)",
    warning: "var(--background-warning-weak)",
  };
  const styles = getStyles(noBorderRadius);

  return (
    <div
      className={css(styles.sectionWrapper, {
        background: colorsByType[type],
        alignItems: children ? "flex-start" : "center",
      })}
      data-testid={testID}
    >
      {icon && (
        <div
          className={css(styles.sectionIconWrapper, {
            alignItems: children ? "flex-start" : "center",
          })}
        >
          <Icon
            padding="space-05"
            name={icon}
            type="standard"
            color={iconColorsByType[type] as CascadiaColor}
          />
        </div>
      )}
      <div>
        {sectionTitle && (
          <Text
            as="div"
            style={
              fontSize === "md"
                ? "global/heading-medium"
                : "global/heading-small"
            }
          >
            {sectionTitle}
          </Text>
        )}
        {children && (
          <Text
            className={css(styles.sectionBodyStyles)}
            style={
              fontSize === "md"
                ? "global/body-primary"
                : "global/body-secondary"
            }
            as="div"
          >
            {children}
          </Text>
        )}
      </div>
    </div>
  );
}

function getStyles(noBorderRadius?: boolean) {
  return {
    sectionWrapper: css({
      borderRadius: !noBorderRadius ? "var(--border-radius-2)" : undefined,
      padding: "var(--space-4)",
      display: "flex",
      boxSizing: "border-box",
    }),
    sectionBodyStyles: css({
      marginTop: "var(--space-1)",
    }),
    sectionIconWrapper: css({
      display: "flex",
      marginRight: "var(--space-2)",
    }),
  };
}
